import {
  Component,
  PLATFORM_ID,
  afterNextRender,
  computed,
  inject,
  input,
  output,
  signal,
  viewChild,
} from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import type { EChartsOption } from 'echarts';
import { EchartComponent } from '@shared/components/echart/echart.component';
import { echartsOptionsToTableModel } from '@shared/utils/echarts-table.util';
import { ChartSeriesSelectComponent } from '@shared/components/chart-series-select/chart-series-select.component';
import {
  ChartToolbarComponent,
  type ChartCardView,
  type ChartExportRequest,
} from '@shared/components/chart-toolbar/chart-toolbar.component';
import { DataTableComponent } from '@shared/components/data-table/data-table.component';
import { DataSourceComponent } from '@shared/components/data-source/data-source.component';
import type { TableModel } from '@shared/utils/table-model';
import { slugifyFileName } from '@shared/utils/table-export.util';
import { runVisualizationExport } from '@shared/utils/visualization-export';

/**
 * Card de visualização compartilhado (DataStories, Nuvem de Perguntas).
 *
 * Junta o gráfico, a tabela de dados e a barra de exports num só lugar. Quem já tem a
 * tabela pronta passa `tableModel`; do contrário ela é derivada das options do ECharts.
 */
@Component({
  selector: 'app-chart-card',
  standalone: true,
  imports: [
    TranslocoPipe,
    EchartComponent,
    ChartSeriesSelectComponent,
    ChartToolbarComponent,
    DataTableComponent,
    DataSourceComponent,
  ],
  template: `
    <section
      class="rounded-3xl border border-border bg-card shadow-[0_1px_3px_rgba(0,0,0,0.04),0_14px_40px_-16px_rgba(0,0,0,0.10)]"
      [class.mt-8]="!flushTop()"
      [attr.aria-label]="ariaLabel() || title()"
    >
      @if (title() || subtitle() || hasChartToolbar()) {
        <header class="flex flex-col gap-2 border-b border-border px-6 py-3">
          @if (hasChartToolbar()) {
            <app-chart-toolbar
              class="w-full"
              [view]="view()"
              [context]="view()"
              [showViewToggle]="showViewToggle()"
              [canExportChart]="!!chartOptions()"
              [canExportTable]="!!tableModel()"
              [busy]="exporting()"
              [showShare]="true"
              [shareTitle]="shareTitle() || title()"
              [shareUrl]="shareUrl()"
              (viewChange)="setView($event)"
              (exportRequest)="onExport($event)"
            >
              @if (showSeriesSelector()) {
                <app-chart-series-select
                  [seriesNames]="seriesNames()"
                  [selected]="visibleSeries()"
                  [selectionLimit]="seriesSelectionLimit()"
                  [disabled]="view() !== 'chart'"
                  (selectedChange)="visibleSeriesChange.emit($event)"
                />
              }
            </app-chart-toolbar>
          }
          @if (title() || subtitle()) {
            <div class="w-full">
              @if (title()) {
                <h3 class="m-0 font-display text-base font-semibold leading-snug tracking-tight text-foreground">{{ title() }}</h3>
              }
              @if (subtitle()) {
                <p class="mt-0.5 text-xs text-muted-foreground">{{ subtitle() }}</p>
              }
            </div>
          }
        </header>
      }

      <div class="p-6">
        @if (chartOptions()) {
          <div
            class="chart-card-viewport relative w-full"
            [style.height.px]="chartHeight()"
            [style.min-height.px]="chartHeight()"
          >
            @if (view() === 'chart') {
              <div class="h-full w-full">
                @if (showChart()) {
                  <app-echart [options]="chartOptions()!" [hideTitle]="!!title()" />
                }
              </div>
              <ng-content select="[data-chart-overlay]" />
            } @else if (tableModel(); as model) {
              <app-data-table
                class="h-full"
                [model]="model"
                [caption]="title()"
                (viewModelChange)="tableView.set($event)"
              />
            } @else {
              <div class="flex h-full items-center justify-center">
                <p class="text-center text-sm text-muted-foreground">
                  {{ 'shared.dataTable.empty' | transloco }}
                </p>
              </div>
            }
          </div>
        } @else {
          <ng-content />
        }
      </div>

      @if (source() || updatedAt()) {
        <app-data-source [source]="source()" [updatedAt]="updatedAt()" />
      }
    </section>
  `,
})
export class ChartCardComponent {
  private readonly transloco = inject(TranslocoService);

  readonly title = input<string>('');
  readonly subtitle = input<string>('');
  readonly source = input<string>('');
  readonly updatedAt = input<string>('');
  readonly ariaLabel = input<string>('');
  readonly chartOptions = input<EChartsOption | null>(null);
  /** Tabela pronta; quando ausente é derivada das options do gráfico. */
  readonly table = input<TableModel | null>(null);
  readonly exportFileName = input<string>('');
  readonly shareTitle = input<string>('');
  readonly shareUrl = input<string>('');
  readonly showViewToggle = input(true);
  /** Altura do canvas em pixels — garante dimensão antes do ECharts inicializar. */
  readonly chartHeight = input(420);
  readonly showSeriesSelector = input(false);
  readonly seriesNames = input<string[]>([]);
  readonly visibleSeries = input<string[]>([]);
  readonly seriesSelectionLimit = input(5);
  readonly visibleSeriesChange = output<string[]>();
  /** Remove o `mt-8` padrão — útil quando o card já está em um wrapper com espaçamento. */
  readonly flushTop = input(false);

  private readonly echart = viewChild(EchartComponent);

  protected readonly isBrowser = isPlatformBrowser(inject(PLATFORM_ID));
  protected readonly showChart = signal(false);
  protected readonly view = signal<ChartCardView>('chart');
  protected readonly exporting = signal(false);
  /** Tabela como está na tela (transposta / colunas escolhidas) — é o que o export leva. */
  protected readonly tableView = signal<TableModel | null>(null);

  constructor() {
    if (this.isBrowser) {
      afterNextRender(() => {
        requestAnimationFrame(() => this.showChart.set(true));
      });
    }
  }

  protected readonly hasChartToolbar = computed(() => !!this.chartOptions());

  protected readonly tableModel = computed<TableModel | null>(() => {
    const provided = this.table();
    if (provided) return provided;

    const options = this.chartOptions();
    if (!options) return null;
    const locale = this.transloco.getActiveLang() === 'en' ? 'en' : 'pt';
    return echartsOptionsToTableModel(options, locale);
  });

  protected setView(mode: ChartCardView): void {
    this.view.set(mode);
  }

  protected async onExport(format: ChartExportRequest): Promise<void> {
    if (!this.isBrowser) return;
    this.exporting.set(true);
    try {
      await runVisualizationExport(format, {
        chart: () => this.echart()?.getChart() ?? null,
        chartOption: () => this.chartOptions(),
        chartSize: () => this.echart()?.getSize() ?? { width: 960, height: this.chartHeight() },
        table: () => this.tableView() ?? this.tableModel(),
        fileName: () =>
          this.exportFileName() || slugifyFileName(this.shareTitle() || this.title() || 'dados'),
        sheetName: () => this.title() || 'Dados',
        documentTitle: () => this.title() || this.shareTitle() || '',
        caption: () => {
          const label = (key: string) => this.transloco.translate(`shared.tableExport.${key}`);
          return {
            title: [this.title(), this.subtitle()].filter(Boolean).join('\n'),
            source: this.source(),
            updatedAt: this.updatedAt(),
            sourceLabel: this.transloco.translate('shared.dataSource.source'),
            updatedAtLabel: this.transloco.translate('shared.dataSource.lastUpdate'),
            fieldLabels: {
              title: label('title'),
              notes: label('notes'),
              country: label('country'),
              codes: label('codes'),
              missingValue: label('missingValue'),
            },
          };
        },
      });
    } finally {
      this.exporting.set(false);
    }
  }
}
